// src/mobile_pages/contract/Info.jsx

import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getContracts } from "../../api/contract/info";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";
import { formatDate } from "../../utils/date";
import Info_view from "./Info_view";

export default function Info() {
  const { id: projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const constructionType = queryParams.get("construction_type") || "";

  const [q, setQ] = useState("");
  const [searchField, setSearchField] = useState("title");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);

  const [showFilters, setShowFilters] = useState(false);

  // 필터 상태 (날짜 필터 추가)
  const [dateFilter, setDateFilter] = useState("updated_at");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const applySearch = () => {
    setPage(1);
    setSearchTrigger((prev) => prev + 1);
  };

  const handleReset = () => {
    setQ("");
    setSearchField("title");
    setDateFilter("updated_at");
    setStartDate(null);
    setEndDate(null);
    setPageSize(15);
    setPage(1);
    setSearchTrigger((prev) => prev + 1);
  };

  const fetchContracts = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = {
        project: projectId,
        construction_type: constructionType,
        [searchField]: q || undefined,
        // 날짜 필터 추가
        date_filter: dateFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page: page,
        page_size: pageSize,
      };

      const res = await getContracts(projectId, params);
      const list = res?.results ?? res;
      setContracts(list);
      setTotal(res.count ?? res.total ?? list?.length ?? 0);
      setErrMsg("");
    } catch (e) {
      console.error(e);
      setErrMsg("목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [
    projectId,
    constructionType,
    page,
    pageSize,
    searchTrigger,
    dateFilter,
    startDate,
    endDate,
  ]);

  const handleKeyPress = (e) => {
    if (e.key === "Enter") applySearch();
  };

  const handleRowClick = (contractId) => setSelectedId(contractId);

  const handleCreate = () => {
    navigate("new", { state: { construction_type: constructionType } });
  };

  const rows = useMemo(
    () => (Array.isArray(contracts) ? contracts : []),
    [contracts]
  );

  // 스켈레톤 로딩 컴포넌트
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg p-4 animate-pulse border">
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
        <div className="h-8 w-8 bg-gray-100 rounded-full" />
      </div>
      <div className="mt-3 flex justify-between">
        <div className="h-3 bg-gray-100 rounded w-1/3" />
        <div className="h-3 bg-gray-100 rounded w-1/4" />
      </div>
    </div>
  );

  return (
    <div className="mobile-page-root mb-16">
      <div className="mobile-page-root-inner space-y-3">
        {/* 헤더 */}
        <div className="bg-white rounded-lg p-4 border min-w-0">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="min-w-0 flex-1 overflow-hidden">
              <h2 className="text-lg font-bold text-gray-900 truncate">
                {constructionType || "전체"} 계약 정보
              </h2>
              <p className="text-gray-500 text-xs mt-0.5">총 {total}건</p>
            </div>
            <button onClick={handleCreate} className="btn-create flex-shrink-0">
              등록
            </button>
          </div>
        </div>

        {errMsg && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
            {errMsg}
          </div>
        )}

        {/* 검색 영역 */}
        <div className="bg-white rounded-lg border overflow-hidden min-w-0">
          <div className="p-3 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex-1 min-w-0 relative">
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="계약명, 업체명 검색..."
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <button
                onClick={applySearch}
                className="btn-search flex-shrink-0"
              >
                검색
              </button>
            </div>
          </div>

          {/* 필터 토글 */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full px-4 py-2 bg-gray-50 border-t text-xs text-gray-600 flex items-center justify-center"
          >
            <span>상세 필터</span>
          </button>

          {showFilters && (
            <div className="px-3 pb-3 space-y-2 border-t bg-gray-50 min-w-0">
              <div className="pt-3 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <select
                    value={searchField}
                    onChange={(e) => setSearchField(e.target.value)}
                    className="col-span-1 bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs min-w-0"
                  >
                    <option value="title">제목</option>
                    <option value="contract_code">계약코드</option>
                    <option value="confirmer_company_name">업체명</option>
                  </select>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="col-span-1 bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs min-w-0"
                  >
                    <option value={15}>15건</option>
                    <option value={30}>30건</option>
                    <option value={50}>50건</option>
                  </select>
                  <button
                    onClick={handleReset}
                    className="col-span-1 flex items-center justify-center bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs text-gray-600 min-w-0"
                  >
                    초기화
                  </button>
                </div>
                {/* 날짜 필터 추가 */}
                <div className="space-y-2">
                  <div className="text-[10px] font-medium text-gray-500">
                    날짜 필터
                  </div>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs min-w-0"
                  >
                    <option value="updated_at">수정일</option>
                    <option value="created_at">등록일</option>
                    <option value="signed_date_actual">계약 체결일</option>
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={startDate || ""}
                      onChange={(e) => setStartDate(e.target.value || null)}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs min-w-0"
                      placeholder="시작일"
                    />
                    <input
                      type="date"
                      value={endDate || ""}
                      onChange={(e) => setEndDate(e.target.value || null)}
                      className="w-full bg-white border border-gray-200 rounded-lg px-2 py-2 text-xs min-w-0"
                      placeholder="종료일"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 계약 리스트 */}
        <div className="space-y-2 min-w-0">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : rows.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <p className="text-sm text-gray-500">등록된 계약이 없습니다.</p>
              <button onClick={handleCreate} className="btn-create mt-4">
                새 계약 등록
              </button>
            </div>
          ) : (
            rows.map((row) => (
              <button
                key={row.dns}
                onClick={() => handleRowClick(row.dns)}
                className="w-full text-left bg-white rounded-lg p-4 border hover:border-blue-300 active:bg-gray-50 transition-all overflow-hidden min-w-0"
              >
                <div className="flex items-start justify-between gap-3 min-w-0">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {row.title || "제목 없음"}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-xs text-gray-500 truncate">
                        {row.meta?.confirmer_company_name || "업체 미입력"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2 min-w-0">
                  {row.meta?.contract_code && (
                    <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded text-[11px]">
                      {row.meta.contract_code}
                    </span>
                  )}
                  {row.meta?.amount && (
                    <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded text-[11px]">
                      {Number(row.meta.amount).toLocaleString("ko-KR")}원
                    </span>
                  )}
                  {row.meta?.signed_date_actual && (
                    <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded text-[11px]">
                      {formatDate(row.meta.signed_date_actual)}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* 페이지네이션 */}
        {!loading && rows.length > 0 && (
          <div className="bg-white rounded-lg p-3 border min-w-0">
            <MobileBoardPagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
            />
          </div>
        )}

        {/* 선택된 계약 상세 - 바텀시트 */}
        {selectedId && (
          <div
            className="fixed inset-0 z-50 bg-black/40"
            onClick={() => setSelectedId(null)}
          >
            <div
              className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white pt-3 pb-2 px-4 border-b z-10">
                <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-3" />
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold">계약 상세</h3>
                  <button
                    onClick={() => setSelectedId(null)}
                    className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg"
                  >
                    닫기
                  </button>
                </div>
              </div>
              <div className="p-4">
                <Info_view
                  contractId={selectedId}
                  onClose={() => setSelectedId(null)}
                  onUpdated={(newId) => {
                    setSelectedId(newId);
                    fetchContracts();
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
